import React, { type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Loader } from '@vibe/core'
import { useAuth } from '../../contexts/AuthContext'

interface AuthGuardProps {
  children: ReactNode
  requireAuth?: boolean
}

/**
 * AuthGuard component for protecting routes
 * - If requireAuth is true (default), redirects unauthenticated users to login
 * - If requireAuth is false, redirects authenticated users away from login
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requireAuth = true,
}) => {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader size="large" />
      </div>
    )
  }

  // Handle login page access
  if (!requireAuth) {
    if (isAuthenticated) {
      // Redirect authenticated users away from login
      const from = location.state?.from?.pathname || '/dashboard'
      return <Navigate to={from} replace />
    }
    return <>{children}</>
  }

  // Handle protected routes
  if (!isAuthenticated) {
    // Save the attempted location for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}
